// Helper functions for error handling
import {AppError} from '../Data/config/AppError';
import {AxiosError} from 'axios';
import type {BaseResponse} from '../Data/types/BaseResponse';

/**
 * Clean error message - remove JSON if concatenated
 */
export function cleanErrorMessage (message: string): string {
  if (!message || typeof message !== 'string') {
    return message || 'حدث خطأ غير متوقع';
  }

  // Remove JSON if concatenated: "text{...JSON...}" -> "text"
  const jsonIndex = message.indexOf('{');
  if (jsonIndex > 0) {
    const afterJson = message.substring(jsonIndex);
    if (afterJson.includes('stat_code') ||
        afterJson.includes('stat_message') ||
        afterJson.includes('"data"') ||
        afterJson.includes('"error"')) {
      return message.substring(0, jsonIndex).trim();
    }
  }

  return message.trim();
}

/**
 * Create AppError from API response data
 * This is the standard way to convert API errors to AppError
 */
export function createAppErrorFromResponse (
  data: BaseResponse<any>,
  defaultMessage: string
): AppError {
  const cleanMessage = cleanErrorMessage(data.stat_message || defaultMessage);

  const mockAxiosError: AxiosError<any> = {
    response: {
      data: {...data, stat_message: cleanMessage},
      status: data.stat_code,
      statusText: '',
      headers: {},
      config: {} as any
    },
    isAxiosError: true,
    name: 'AxiosError',
    message: cleanMessage,
    config: {} as any,
    toJSON: () => ({})
  };

  return AppError.fromApiError(mockAxiosError);
}

/**
 * Standard catch block for API Data Sources
 * Re-throws errors without handling (component layer handles errors)
 */
export function handleDataSourceError (error: unknown): never {
  if (error instanceof AppError) {
    throw error;
  }
  throw AppError.fromApiError(error);
}

/**
 * Check if API response has error and throw AppError
 * Returns true if success, throws if error
 */
export function checkApiResponse<T> (
  data: BaseResponse<T>,
  defaultMessage: string,
  successCodes: number[] = [200]
): void {
  if (!successCodes.includes(data.stat_code)) {
    throw createAppErrorFromResponse(data, defaultMessage);
  }
}

/**
 * Unwrap API response data, checking status and returning data
 * Throws AppError if response is not successful
 */
export function unwrapResponse<T> (
  raw: BaseResponse<T>,
  defaultMessage: string,
  successCodes: number[] = [200]
): T {
  checkApiResponse(raw, defaultMessage, successCodes);
  return raw.data;
}

